import { api } from '../console/api.ts';
import { empty, pageHead, pill } from '../console/components.ts';
import { esc, icon, when } from '../console/format.ts';
import { MODULES, type Module, moduleLabel } from '../console/modules.ts';
import { type Actions, current, render } from '../console/router.ts';
import { administersNubi } from '../console/session.ts';
import { act } from '../console/ui.ts';
import { selectControl } from '../controls.ts';
import type { PlatformAgency } from './agencias.ts';

/**
 * «Administración de Nubi › Módulos por agencia» (#285, ADR-0029): the peripheral modules of one
 * agency — Estadísticas, Metas, Prospectos, Integración con G&T, Conocimiento, Onboarding,
 * Antecedentes, Cursos, Boleta y Recordatorios —, each on or off. Turning one off is confirmed on
 * the screen (never a browser dialog) saying what it means: it leaves that agency's menu and its
 * API refuses it; its data is kept and the automatic jobs go on. Turning it on again is one click.
 * The modules that move money are not here: they are always on. Every change is in «Bitácora».
 */

export type AgencyModule = { module: Module; enabled: boolean; updatedAt: string | null };
export type AgencyModules = { agencyId: string; modules: AgencyModule[] };

/** The agency on screen ('' until the first paint picks the first one). */
let agencyId = '';
/** The module waiting for the confirmation of turning it off. */
let confirming: Module | null = null;
/** The agencies of the last paint (names for the toasts and the confirmation). */
let agencies: PlatformAgency[] = [];

/** Back to the first agency (a new session, or the menu). */
export function resetModulosAgencia() {
  agencyId = '';
  confirming = null;
  agencies = [];
}

const ALWAYS_ON =
  'Pólizas, Caja, Comisiones, Liquidaciones y Remesas no aparecen aquí: mueven dinero y siempre están encendidos.';
const OFF_MEANS =
  'Sale del menú de la agencia y su API responde que el módulo está apagado. Sus datos se conservan y los envíos automáticos siguen.';
const SECONDS = 'El cambio llega a la agencia en unos segundos.';

const nameOf = (id: string) => agencies.find((a) => a.id === id)?.name ?? id;
const stateText = (on: boolean) => (on ? 'Encendido' : 'Apagado');

/** The confirmation of turning a module off: what it means for the agency, then a deliberate click. */
function confirmHtml(module: Module) {
  const m = MODULES.find((x) => x.module === module);
  return `<section class="card" role="alertdialog" aria-modal="false" aria-labelledby="module-confirm-title" aria-describedby="module-confirm-text" data-component="module-confirm">
    <div class="card-head"><h2 id="module-confirm-title" tabindex="-1">¿Apagar ${esc(moduleLabel(module))} para ${esc(nameOf(agencyId))}?</h2></div>
    <p class="note warn" id="module-confirm-text">${icon('alert')}<span>${esc(`${m?.covers ?? ''} ${OFF_MEANS} ${SECONDS}`.trim())}</span></p>
    <div class="form-foot"><span class="spacer"></span>
      <button type="button" class="btn" data-module-cancel>Cancelar</button>
      <button type="button" class="btn danger" data-module-off-confirm="${esc(module)}">Sí, apagar</button></div>
  </section>`;
}

export async function modulosAgencia() {
  const description =
    'Los módulos periféricos de cada agencia: apaga los que no usa y vuelve a encenderlos cuando los necesite.';
  const head = pageHead('Módulos por agencia', description);
  if (!administersNubi())
    return `${head}${empty('lock', 'Esta sección es de la administración de Nubi', 'Tu rol no administra los módulos de las agencias.', '', 2)}`;
  agencies = await api<PlatformAgency[]>('GET', '/v1/platform/agencies');
  if (agencies.length === 0)
    return `${head}${empty('building', 'Todavía no hay agencias', 'Da de alta una agencia en «Agencias» para elegir sus módulos.', '', 2)}`;
  if (!agencies.some((a) => a.id === agencyId)) agencyId = agencies[0]?.id ?? '';
  const { modules } = await api<AgencyModules>(
    'GET',
    `/v1/platform/agencies/${encodeURIComponent(agencyId)}/modules`,
  );
  const picker = selectControl(
    { id: 'modules-agency', label: 'Agencia' },
    agencies.map((a) => ({ value: a.id, label: a.name, selected: a.id === agencyId })),
  );
  const off = modules.filter((m) => !m.enabled).length;
  const rows = modules
    .map((m) => {
      const label = moduleLabel(m.module);
      const covers = MODULES.find((x) => x.module === m.module)?.covers ?? '';
      const action = m.enabled
        ? `<button type="button" class="btn sm danger" data-module-off="${esc(m.module)}" aria-label="Apagar ${esc(label)} para ${esc(nameOf(agencyId))}">Apagar</button>`
        : `<button type="button" class="btn sm primary" data-module-on="${esc(m.module)}" aria-label="Encender ${esc(label)} para ${esc(nameOf(agencyId))}">Encender</button>`;
      return `<tr data-agency-module="${esc(m.module)}"><th scope="row">${esc(label)}</th><td>${esc(covers)}</td>
        <td>${m.enabled ? pill('ok', stateText(true)) : pill('bad', stateText(false))}${m.updatedAt ? `<div class="muted small">Desde el ${esc(when(m.updatedAt))}</div>` : ''}</td>
        <td class="actions">${action}</td></tr>`;
    })
    .join('');
  return `${head}${confirming ? confirmHtml(confirming) : ''}<section class="card" data-component="agency-modules">
    <div class="card-head"><h2>Módulos de ${esc(nameOf(agencyId))}</h2><span class="spacer"></span><span class="muted small" aria-live="polite">${off === 0 ? 'Todos encendidos' : `${off} ${off === 1 ? 'apagado' : 'apagados'}`}</span></div>
    <div class="toolbar" role="group" aria-label="Agencia"><div class="field">${picker}</div></div>
    <p class="note info">${icon('shield')}<span>${esc(`${ALWAYS_ON} ${SECONDS}`)}</span></p>
    <div class="table-wrap" data-component="table"><table class="table"><caption class="sr-only">Módulos periféricos de ${esc(nameOf(agencyId))} y si están encendidos</caption>
    <thead><tr><th scope="col">Módulo</th><th scope="col">Qué incluye</th><th scope="col">Estado</th><th scope="col" class="actions">Acción</th></tr></thead>
    <tbody>${rows}</tbody></table></div></section>`;
}

/** Paints the screen again and puts the focus where the person continues. */
async function repaint(focus?: string) {
  await render(current, { quiet: true });
  if (focus) document.querySelector<HTMLElement>(focus)?.focus();
}
const isModule = (m: string | undefined): m is Module => MODULES.some((x) => x.module === m);

/** Turns a module of the agency on screen off or on, then the list again. */
const setModule = (b: HTMLElement, module: Module, enabled: boolean) => {
  const agency = agencyId;
  const name = nameOf(agency);
  void act(b, enabled ? 'Encendiendo…' : 'Apagando…', async () => {
    await api(
      'PUT',
      `/v1/platform/agencies/${encodeURIComponent(agency)}/modules/${encodeURIComponent(module)}`,
      { enabled },
    );
    confirming = null;
    await repaint(`[data-agency-module="${module}"] button`);
    return enabled
      ? `${moduleLabel(module)} quedó encendido para ${name}.`
      : `${moduleLabel(module)} quedó apagado para ${name}: sale de su menú.`;
  });
};

export const actions: Actions = {
  click: {
    moduleOff: (_b, d) => {
      if (!isModule(d.moduleOff)) return;
      confirming = d.moduleOff;
      void repaint('#module-confirm-title');
    },
    moduleCancel: () => {
      const back = confirming;
      confirming = null;
      void repaint(back ? `[data-agency-module="${back}"] button` : undefined);
    },
    moduleOffConfirm: (b, d) => {
      if (isModule(d.moduleOffConfirm)) setModule(b, d.moduleOffConfirm, false);
    },
    moduleOn: (b, d) => {
      if (isModule(d.moduleOn)) setModule(b, d.moduleOn, true);
    },
  },
  change: {
    'modules-agency': (control) => {
      agencyId = control.value;
      confirming = null;
      void repaint('#modules-agency');
    },
  },
};
